// Служебные эндпоинты и ошибки клиента — tech/03 §17, §18; E01-T01, E01-T10.
import { z } from 'zod';
import { EpochMs, IsoTime, strict } from '../common.js';
import { WsEvent } from '../ws.js';
import { endpoint } from '../endpoint.js';

/** Без конверта ошибки: балансировщик читает только статус (03 §18). */
export const healthLive = endpoint({
  id: 'system.health.live', method: 'GET', path: '/health/live', auth: false, signed: false, idempotency: 'read', rate: 'none',
  response: strict({ ok: z.literal(true) }), status: 200, errors: [], spec: 'tech/03 §18', task: 'E01-T01',
});

export const ReadyResponse = strict({ ok: z.boolean(), checks: z.record(z.string(), z.enum(['ok', 'fail'])) });

export const healthReady = endpoint({
  id: 'system.health.ready', method: 'GET', path: '/health/ready', auth: false, signed: false, idempotency: 'read', rate: 'none',
  response: ReadyResponse, status: 200, errors: [], spec: 'tech/03 §18 (503 — тело той же формы с ok=false)', task: 'E01-T01',
});

export const MetaResponse = strict({
  minBuild: z.string(),
  latestBuild: z.string(),
  maintenance: strict({ from: IsoTime, to: IsoTime }).optional(),
  features: z.record(z.string(), z.boolean()),
  content: strict({ release: z.string(), manifestUrl: z.url() }).nullable(),
});

export const getMeta = endpoint({
  id: 'system.meta', method: 'GET', path: '/meta', auth: false, signed: false, idempotency: 'read', rate: 'auth',
  response: MetaResponse, status: 200, errors: [], spec: 'tech/03 §18; E01-T01', task: 'E01-T01',
});

/** Ошибки клиента (03 §17): без подписи запроса, но с JWT; ≤ 20 штук, тело ≤ 64 КБ. */
export const clientErrors = endpoint({
  id: 'system.client_errors', method: 'POST', path: '/client-errors', auth: true, signed: false, idempotency: 'none', rate: 'analytics',
  body: strict({
    sessionId: z.string().min(1).max(64),
    errors: z.array(strict({
      kind: z.string().min(1).max(32),
      message: z.string().max(2000),
      stack: z.string().max(16_000),
      screen: z.string().max(64),
      build: z.string().max(32),
    })).min(1).max(20),
  }),
  response: strict({ accepted: z.int().nonnegative() }), status: 200, errors: [], spec: 'tech/03 §17', task: 'E01-T10',
});

/**
 * Запасной путь без WS (03 §18, §19; E01-T05): клиент опрашивает раз в 10 с, если WS не поднялся 3 раза подряд.
 * Те же конверты, что в WS; `gap: true` — `since` старше 15 мин или вытеснен из `ev:{pid}`: нужен `GET /bootstrap`.
 */
export const getSync = endpoint({
  id: 'system.sync', method: 'GET', path: '/sync', auth: true, signed: false, idempotency: 'read', rate: 'sync',
  query: strict({ since: z.coerce.number().int().nonnegative() }),
  response: strict({ events: z.array(WsEvent).max(50), lastSeq: z.int().nonnegative(), serverTime: EpochMs, gap: z.boolean() }),
  status: 200, errors: [], spec: 'tech/03 §18, §19', task: 'E01-T05',
});
